import React, { Component, Fragment } from 'react';
import { Container, Row, Col } from 'react-grid-system';

import { Select } from 'components/cosmos';
import { DatePicker } from 'components/date';
import { Form, FormControl, FormSubmit } from 'components/form';
import { Grid } from 'components/grid';
import { InputGroup, InputBlock } from 'components/input';
import { I18n, translate } from 'components/translate';
import ClubDate from 'helpers/ClubDate';
import { translatedLanguageList } from 'helpers/Language';
import { setEmptyPropertyToNull } from 'helpers/Object';
import isNil from 'lodash/isNil';
import trim from 'lodash/trim';
import { inject } from 'mobx-react';
import { ClassesService } from 'modules/content';
import UserService from 'modules/user/UserService';
import { ImportService } from 'modules/userManager/imports';
import PropTypes from 'prop-types';

import DescriptionImport from './DescriptionImport';
import SaveUsers from './SaveUsers';

import './Import.scss';

const getValidation = (fieldErrorName) => {
  return fieldErrorName
    ? { required: <I18n label="validations.required" /> }
    : '';
};

@translate()
@inject((stores) => ({
  limit: stores.UserStore.limit
}))
export default class ManuallyImport extends Component {
  static propTypes = {
    fieldErrorName: PropTypes.string,
    t: PropTypes.func,
    limit: PropTypes.number
  };

  static defaultProps = {
    fieldErrorName: ''
  };

  state = {
    name: '',
    email: '',
    locale: '',
    role: '',
    membershipClass: '',
    purchaseDate: '',
    classesList: [],
    saving: false
  };

  async componentDidMount() {
    const classesList = await ClassesService.getSelectFormatedClasses();
    const limit = await ImportService.getImportLimit();

    UserService.initLimit(limit);
    this.setState({ classesList, membershipClass: classesList[0] });
  }

  prepareData = (data) => {
    const { locale, membershipClass, purchaseDate, role } = this.state;

    delete data.date;

    data.name = trim(data.name);
    data.email = trim(data.email);

    if (locale) {
      data.locale = locale.value;
    }

    if (membershipClass) {
      data.membershipClass = membershipClass.value;
    }

    if (purchaseDate) {
      data.purchaseDate = new Date(purchaseDate).toISOString();
    }

    if (role) {
      data.role = role.value;
    }

    return data;
  };

  onSubmit = async (data) => {
    this.setState({ saving: true });

    data = setEmptyPropertyToNull(this.prepareData(data));

    try {
      await ImportService.importManually(data);
      UserService.decrementLimit();
      this.setState({
        name: '',
        email: '',
        locale: '',
        role: '',
        purchaseDate: '',
        saving: false
      });
      this.formNode.clear();
    } catch {
      this.setState({ saving: false });
    }
  };

  setLanguage = (value) => {
    this.setState({
      locale: value
    });
  };

  setRole = (value) => {
    this.setState({
      role: value
    });
  };

  setClass = (value) => {
    this.setState({
      membershipClass: value
    });
  };

  onDateChange = (timestamp) => {
    const date = ClubDate.resolve(timestamp);
    let purchaseDate = '';

    if (date) {
      const dateWithTime = date.at.startOfDay();

      purchaseDate = dateWithTime.to.timestamp();
    }

    this.setState({ purchaseDate });
  };

  get rolesList() {
    return [
      {
        value: 'ADMIN',
        label: this.props.t('users_import.roles.administrator')
      },
      {
        value: 'CONTENT_EDITOR',
        label: this.props.t('users_import.roles.content_editor')
      },
      { value: 'STUDENT', label: this.props.t('users_import.roles.student') },
      {
        value: 'MODERATOR',
        label: this.props.t('users_import.roles.moderator')
      }
    ];
  }

  render() {
    const { fieldErrorName, limit, t } = this.props;
    const error = getValidation(fieldErrorName);
    const submitted = fieldErrorName;

    return (
      <Fragment>
        <Form onSubmit={this.onSubmit} ref={(node) => (this.formNode = node)}>
          <Grid block>
            <h2>{t('users_import.manually.title')}</h2>
            <label className="label__subtitle mb__md">
              {t('users_import.manually.title', { checkOnlineEvent: true })}
            </label>
            {!isNil(limit) && limit >= 0 && (
              <label className="label__subtitle mb__md">
                {t('users_import.batch.number_users_explanation', {
                  data: { amount: limit },
                  checkOnlineEvent: true
                })}
              </label>
            )}
            <Container fluid className="users__form p__0">
              <Row justify="between">
                <Col xs={12} sm={6} md={6} lg={4}>
                  <FormControl>
                    <InputGroup
                      required
                      name="name"
                      value={this.state.name}
                      maxLength="100"
                      autoComplete="off"
                      labelValue="users_import.manually.name.label"
                      placeholder="users_import.manually.name.placeholder"
                      submitted={submitted}
                      error={error}
                      disabled={!limit}
                      className="mb__sm"
                    />
                  </FormControl>
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <FormControl>
                    <InputGroup
                      required
                      name="email"
                      value={this.state.email}
                      maxLength="100"
                      autoComplete="off"
                      labelValue="users_import.manually.email.label"
                      placeholder="users_import.manually.email.placeholder"
                      submitted={submitted}
                      error={error}
                      disabled={!limit}
                      className="mb__sm"
                    />
                  </FormControl>
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <Select
                    name="language"
                    value={this.state.locale}
                    label={t('users_import.manually.language.label')}
                    labelClassName="_mt-0"
                    placeholder={t(
                      'users_import.manually.language.placeholder'
                    )}
                    options={translatedLanguageList()}
                    onChange={this.setLanguage}
                    disabled={!limit}
                  />
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <Select
                    name="role"
                    value={this.state.role}
                    label={t('users_import.manually.role.label')}
                    labelClassName="_mt-0"
                    placeholder={t('users_import.manually.role.placeholder')}
                    options={this.rolesList}
                    onChange={this.setRole}
                    disabled={!limit}
                  />
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <Select
                    name="class"
                    label={t('users_import.manually.class.label')}
                    labelClassName="_mt-0"
                    value={this.state.membershipClass}
                    placeholder={t('users_import.manually.class.placeholder')}
                    options={this.state.classesList}
                    onChange={this.setClass}
                    disabled={!limit}
                    hasSearch={false}
                  />
                </Col>
                <Col xs={12} sm={6} md={6} lg={4}>
                  <FormControl>
                    <InputBlock
                      name="date"
                      labelValue="users_import.manually.date.label"
                      placeholder="users_import.manually.date.placeholder"
                    >
                      <DatePicker
                        date={this.state.purchaseDate}
                        onDateChange={this.onDateChange}
                        className="users-manually__fields__input"
                        isOutsideRange={ImportService.isOutsideRange}
                        disabled={!limit}
                      />
                    </InputBlock>
                  </FormControl>
                </Col>
              </Row>
            </Container>
          </Grid>

          <DescriptionImport />

          <FormSubmit>
            <SaveUsers disabled={this.state.saving || !limit} />
          </FormSubmit>
        </Form>
      </Fragment>
    );
  }
}
